import { useEffect, useRef, useState } from 'react';

import commonIcd from '../../../data/commonIcd.json';
import useIcd10Lookup from '../../../hooks/useIcd10Lookup';
import Btn from '../shared/Btn';
import Icon from '../shared/Icon';
import Section from '../shared/Section';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
const DATALIST_ID_PREFIX = 'icd-list-';

/**
 * Layout matches docs/superpowers/specs/assets/claim-creation-flow-end-to-end/
 * project/sections.jsx::DiagnosisSection — 3-col grid of letter-badge cards
 * with datalist autocomplete, inline description, per-row remove, and
 * pill-style quick-add chips. Wire shape stays canonical:
 * `diagnoses[i] = { id?, diagnosis_index, diagnosis_code }`. We never
 * synthesize a non-UUID `id` (server assigns on first save).
 *
 * M2: autocomplete suggestions and inline descriptions are now driven by
 * the canonical CMS Section 111 ICD-10 table via ``useIcd10Lookup`` — the
 * static ``commonIcd.json`` list is retained only for the quick-add chips.
 *
 * @param {{
 *   claim: any,
 *   setField: (path: string, value: any) => void,
 *   editable: boolean,
 *   errorsByField: Map<string, any>,
 * }} props
 */
export default function DiagnosisSection({ claim, setField, editable, errorsByField }) {
  const diagnoses = claim.diagnoses || [];
  const filledCount = diagnoses.filter((/** @type {any} */ d) => d?.diagnosis_code).length;
  const { lookup, prefetch, searchPrefix } = useIcd10Lookup();

  /** @param {number} idx @param {string} code */
  const update = (idx, code) => {
    const next = [...diagnoses];
    next[idx] = {
      ...(next[idx] || {}),
      diagnosis_index: idx + 1,
      diagnosis_code: code,
    };
    setField('diagnoses', next);
  };

  /** @param {number} idx */
  const removeAt = (idx) => {
    const next = diagnoses
      .filter((/** @type {any} */ _d, /** @type {number} */ i) => i !== idx)
      .map((/** @type {any} */ d, /** @type {number} */ i) => ({ ...d, diagnosis_index: i + 1 }));
    setField('diagnoses', next);
  };

  /** @param {string} seed */
  const addRow = (seed = '') => {
    if (diagnoses.length >= 12) return;
    if (seed && diagnoses.some((/** @type {any} */ d) => d?.diagnosis_code === seed)) return;
    const next = [...diagnoses, { diagnosis_index: diagnoses.length + 1, diagnosis_code: seed }];
    setField('diagnoses', next);
  };

  const rows = diagnoses.length === 0 ? [{}] : diagnoses;

  return (
    <Section
      icon="clipboard-list"
      title="Diagnosis Codes"
      subtitle="ICD-10 codes in priority order (A–L). Used as pointers on line items."
      badge={filledCount || undefined}
      action={editable && diagnoses.length < 12 && (
        <Btn kind="secondary" size="sm" icon="plus" onClick={() => addRow()}>Add</Btn>
      )}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {rows.map((/** @type {any} */ d, /** @type {number} */ i) => (
          <DiagnosisRow
            key={d?.id || i}
            index={i}
            code={d?.diagnosis_code || ''}
            editable={editable}
            canRemove={editable && diagnoses.length > 1}
            error={errorsByField?.get(`diagnoses.${i}.diagnosis_code`)?.message}
            lookup={lookup}
            prefetch={prefetch}
            searchPrefix={searchPrefix}
            onChange={(v) => update(i, v)}
            onRemove={() => removeAt(i)}
          />
        ))}
      </div>

      {editable && (/** @type {any[]} */ (commonIcd)).length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <span style={{
            fontSize: 12, color: 'var(--ab-fg-subtle)',
            alignSelf: 'center', marginRight: 4,
          }}>Quick add:</span>
          {(/** @type {any[]} */ (commonIcd)).slice(0, 6).map((/** @type {any} */ c) => (
            <span
              key={c.code}
              role="button"
              onClick={() => addRow(c.code)}
              style={{
                fontSize: 11,
                padding: '3px 8px',
                borderRadius: 999,
                border: '1px solid var(--ab-border-hair)',
                cursor: 'pointer',
                color: 'var(--ab-fg-muted)',
                background: 'var(--ab-bg)',
                userSelect: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ab-bg-alt)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--ab-bg)'; }}
            >
              {c.code}
            </span>
          ))}
        </div>
      )}
    </Section>
  );
}

/**
 * Single diagnosis row: input + datalist + description + invalid chip.
 *
 * Per-row local state holds the prefix-search suggestions so each input has
 * its own autocomplete population without leaking state between rows.
 *
 * @param {{
 *   index: number,
 *   code: string,
 *   editable: boolean,
 *   canRemove: boolean,
 *   error?: string,
 *   lookup: (code: string) => { status: string, description: string },
 *   prefetch: (code: string) => void,
 *   searchPrefix: (q: string) => Promise<Array<{code: string, description: string}>>,
 *   onChange: (value: string) => void,
 *   onRemove: () => void,
 * }} props
 */
function DiagnosisRow({
  index,
  code,
  editable,
  canRemove,
  error,
  lookup,
  prefetch,
  searchPrefix,
  onChange,
  onRemove,
}) {
  /** @type {[Array<{code: string, description: string}>, Function]} */
  const [suggestions, setSuggestions] = useState([]);
  const requestRef = useRef(0);

  // Trigger the per-code lookup from an effect so the hook's forceUpdate
  // never fires during this row's render (would otherwise be a cross-
  // component state update — see useIcd10Lookup.js doc).
  useEffect(() => {
    if (code) prefetch(code);
  }, [code, prefetch]);

  useEffect(() => {
    if (!editable || !code) {
      setSuggestions([]);
      return;
    }
    const myReq = ++requestRef.current;
    let cancelled = false;
    searchPrefix(code, 25).then((results) => {
      if (cancelled || myReq !== requestRef.current) return;
      setSuggestions(results);
    });
    return () => {
      cancelled = true;
    };
  }, [code, editable, searchPrefix]);

  const datalistId = `${DATALIST_ID_PREFIX}${index}`;
  const result = lookup(code);
  const description = result.status === 'known' ? result.description : '';
  const isUnknown = result.status === 'unknown';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <datalist id={datalistId}>
        {suggestions.map((s) => (
          <option key={s.code} value={s.code}>{s.description}</option>
        ))}
      </datalist>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        border: `1px solid ${error || isUnknown ? 'var(--ab-denied)' : code ? 'var(--ab-fg)' : 'var(--ab-border-hair)'}`,
        borderRadius: 'var(--ab-radius-sm)',
        padding: '8px 10px',
        background: 'var(--ab-bg)',
      }}>
        <span style={{
          width: 22, height: 22,
          borderRadius: 4,
          background: 'var(--ab-fg)',
          color: 'var(--ab-fg-inverse)',
          display: 'grid', placeItems: 'center',
          fontSize: 11, fontWeight: 'var(--ab-weight-bold)',
          flexShrink: 0,
        }}>{LETTERS[index]}</span>
        <input
          data-field={`diagnoses.${index}.diagnosis_code`}
          value={code}
          list={datalistId}
          placeholder="ICD-10"
          maxLength={10}
          disabled={!editable}
          onChange={(/** @type {any} */ e) => onChange(e.target.value.toUpperCase())}
          style={{
            flex: 1, minWidth: 0,
            border: 'none', outline: 'none', background: 'transparent',
            fontFamily: 'var(--ab-font-sans)',
            fontSize: 'var(--ab-text-sm)',
            fontWeight: 'var(--ab-weight-semi)',
            color: 'var(--ab-fg)',
          }}
        />
        {description && (
          <span style={{
            fontSize: 11, color: 'var(--ab-fg-subtle)',
            maxWidth: 140, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{description}</span>
        )}
        {canRemove && (
          <span
            role="button"
            aria-label={`Remove diagnosis ${LETTERS[index]}`}
            onClick={onRemove}
            style={{
              cursor: 'pointer', color: 'var(--ab-fg-subtle)',
              display: 'inline-flex', flexShrink: 0,
            }}
          >
            <Icon name="x" size={14} />
          </span>
        )}
      </div>
      {error && (
        <span style={{ fontSize: 11, color: 'var(--ab-denied)' }}>{error}</span>
      )}
      {!error && isUnknown && (
        <span
          data-testid={`icd-unknown-${index}`}
          style={{ fontSize: 11, color: 'var(--ab-denied)' }}
        >
          Not in CMS Oct 2025 list — verify code
        </span>
      )}
    </div>
  );
}
